import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { getTextField } from '@lender/utils';
import { Box, Button } from '@mui/material';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { MarkedAppFormsTreeFieldId } from 'app/services/autoLockForms/autoLockForms.fieldIds';
import FormGeneratorRichTextEditor from 'app/shared/componentsRestyled/common/formGenerator/FormGeneratorRichTextEditor';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { object } from 'yup';

import useForm from 'hooks/useForm';

import {
  selectFormHiddenFieldsMap,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
} from 'store/loanApplications/selectors';

import { UnderwritingTestIds } from '../../Underwriting.testIds';

import { controlsStyles } from './Note.styles';

const schema = object().shape({
  note: getTextField({ isRequired: false }),
});

const NoteForm = ({ isLoading = false, note = '', onCancel = _.noop, onSave = _.noop }) => {
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.NOTES));
  const hiddenFieldsMap = useSelector(selectFormHiddenFieldsMap(FormNames.NOTES));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.NOTES));

  const isDisabledByLock = isLoading || isPageLocked;

  const { form, handleChange, validationErrors, setSchema, isFormChanged, validateForm } = useForm({
    note,
  });

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      onSave(form);
    },
    [form, onSave, validateForm],
  );

  return (
    <form
      noValidate
      className="w-full"
      data-ischanged={isFormChanged}
      data-testid={UnderwritingTestIds.NOTES_FORM}
      onSubmit={onSubmit}
    >
      <FormGeneratorRichTextEditor
        autoFocus
        fullWidth
        multiline
        required
        data-field-id={MarkedAppFormsTreeFieldId.UNDERWRITING_NOTES}
        data-testid={UnderwritingTestIds.NOTES_FORM_NOTES_INPUT}
        disabled={isLoading}
        error={!!validationErrors.note}
        helperText={validationErrors.note}
        id="note"
        isHidden={hiddenFieldsMap.notes}
        label=""
        maxLength={2000}
        name="note"
        readOnly={isDisabledByLock || lockedFieldsMap.notes}
        rows={16}
        type="text"
        value={form.note}
        variant="outlined"
        onChange={handleChange}
      />

      <Box sx={controlsStyles}>
        <Button
          data-testid={UnderwritingTestIds.NOTES_FORM_CANCEL_BUTTON}
          disabled={isLoading}
          size="small"
          variant="outlined"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          data-testid={UnderwritingTestIds.NOTES_FORM_SUBMIT_BUTTON}
          disabled={isDisabledByLock || !isFormChanged}
          size="small"
          type="submit"
        >
          Save
        </Button>
      </Box>
    </form>
  );
};

NoteForm.propTypes = {
  isLoading: PropTypes.bool,
  note: PropTypes.string,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
};

export default NoteForm;
